import { Link } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { DynamicFieldKey_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useMediaQuery } from '@frontend/responsiveness';
import { formatDynamicFieldKey } from '@frontend/string';
import { theme } from '@frontend/theme';
import { useContentComposer } from '../../../content-composer/content-composer-provider';
import { SectionBase } from '../../../content-composer/section-base/section-base';
import type { EmailComposerLinkType } from '../../../content-composer/types';
import { ExpansionButton } from '../expansion-button';
import { PopoverButtonGroup } from '../popover-button-group';
import { ToolbarButtonGroup } from '../toolbar-button-group';
import { ToolbarView } from './types';

const CALL_TO_ACTION_LINK = formatDynamicFieldKey(DynamicFieldKey_Enum.CALL_TO_ACTION_LINK);
const PRACTICE_ADDRESS = formatDynamicFieldKey(DynamicFieldKey_Enum.BUSINESS_GROUP_ADDRESS);
const PRACTICE_PHONE = formatDynamicFieldKey(DynamicFieldKey_Enum.BUSINESS_GROUP_PHONE);
const SCHEDULE_LINK = formatDynamicFieldKey(DynamicFieldKey_Enum.SCHEDULE_LINK);

const LARGE_BREAKPOINT = 1320;
const EXTRA_LARGE_BREAKPOINT = 1500;

type Option = { linkType: EmailComposerLinkType; label: string; url?: string };

type OptionButtonProps = OptionsToolbarProps & {
  option: Option;
};

const OptionButton = ({ link, setLink, openFormsModal, option, setView }: OptionButtonProps) => {
  const handleClick = () => {
    switch (option.linkType) {
      case 'address':
      case 'confirmation':
      case 'phone':
      case 'schedule': {
        const newLink: Link = { ...link, linkType: option.linkType, url: option.url ?? '' };
        setLink(newLink);
        return;
      }

      case 'custom': {
        setView('custom');
        return;
      }

      case 'form': {
        openFormsModal();
        return;
      }
    }
  };

  const getIcon = () => {
    switch (option.linkType) {
      case 'form':
        return 'forms-small';
      case 'address':
        return 'building-small';
      case 'confirmation':
        return 'link-small';
      case 'phone':
        return 'phone-small';
      case 'schedule':
        return 'schedule-pulse-small';
      default:
        return 'link-small';
    }
  };

  return (
    <SectionBase.Toolbar.Button onClick={handleClick}>
      <Icon name={getIcon()} css={{ marginRight: theme.spacing(0.5) }} />
      {option.label}
    </SectionBase.Toolbar.Button>
  );
};

type OptionsToolbarProps = {
  link?: Link;
  openFormsModal: () => void;
  setLink: (newLink: Link) => void;
  setView: (view: ToolbarView) => void;
};

export const OptionsToolbar = (props: OptionsToolbarProps) => {
  const { t } = useTranslation('content-composer');
  const { composerMode } = useContentComposer();

  const options: Option[] = [
    { linkType: 'phone', label: t('Practice Phone'), url: PRACTICE_PHONE },
    { linkType: 'address', label: t('Practice Address'), url: PRACTICE_ADDRESS },
    { linkType: 'schedule', label: t('Schedule'), url: SCHEDULE_LINK },
    { linkType: 'form', label: t('Form') },
    composerMode === 'auto-message'
      ? { linkType: 'confirmation', label: t('Confirmation'), url: CALL_TO_ACTION_LINK }
      : undefined,
    { linkType: 'custom', label: t('Custom') },
  ].filter((option): option is NonNullable<Option> => !!option);

  const alwaysPresentOptions = options.slice(0, 1);
  const mediumScreenSizeOptions = options
    .filter((option) => !alwaysPresentOptions.find((o) => o.linkType === option.linkType))
    .slice(0, 2);
  const largeScreenSizeOptions = options.filter(
    (option) =>
      !alwaysPresentOptions.find((o) => o.linkType === option.linkType) &&
      !mediumScreenSizeOptions.find((o) => o.linkType === option.linkType)
  );
  const isLarge = useMediaQuery(`(min-width: ${LARGE_BREAKPOINT}px) and (max-width: ${EXTRA_LARGE_BREAKPOINT}px)`);

  // TODO: Fix the styling of the dropdown button and items in the menus
  return (
    <>
      <SectionBase.Toolbar.Button onClick={() => props.setView('default')}>
        <Icon name='back-small' />
      </SectionBase.Toolbar.Button>
      {alwaysPresentOptions.map((option) => (
        <OptionButton key={option.linkType} option={option} {...props} />
      ))}
      <ToolbarButtonGroup breakpoint={LARGE_BREAKPOINT}>
        {mediumScreenSizeOptions.map((option) => (
          <OptionButton key={option.linkType} option={option} {...props} />
        ))}
      </ToolbarButtonGroup>
      <ToolbarButtonGroup breakpoint={EXTRA_LARGE_BREAKPOINT}>
        {largeScreenSizeOptions.map((option) => (
          <OptionButton key={option.linkType} option={option} {...props} />
        ))}
      </ToolbarButtonGroup>
      {((mediumScreenSizeOptions.length > 0 && !isLarge) || largeScreenSizeOptions.length > 0) && (
        <ExpansionButton breakpoint={EXTRA_LARGE_BREAKPOINT} hideBorder>
          {mediumScreenSizeOptions.length > 0 ? (
            <PopoverButtonGroup breakpoint={LARGE_BREAKPOINT}>
              {mediumScreenSizeOptions.map((option) => (
                <OptionButton key={option.linkType} option={option} {...props} />
              ))}
            </PopoverButtonGroup>
          ) : null}
          {largeScreenSizeOptions.length > 0
            ? largeScreenSizeOptions.map((option) => <OptionButton key={option.linkType} option={option} {...props} />)
            : null}
        </ExpansionButton>
      )}
    </>
  );
};
